import { useState, useMemo, useCallback, useEffect } from "react";
import { useHR } from "../context/HRContext";
import { useAuthStore } from "@/store/authStore";
import { getCompanySettings } from "../../verve-settings/services/settingsService";
import { API_BASE_URL as BASE_URL } from "@/services/api";

export function usePayslips() {
  const { employees, paySetups, payslips, generatePayslip, bulkGeneratePayslips, markPayslipPaid, deletePayslip, attendance, leaves } = useHR();
  const { companySettings } = useAuthStore();

  const [filterEmp, setFilterEmp] = useState("");
  const [filterMonth, setFilterMonth] = useState("");
  const [filterYear, setFilterYear] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [isGenerateOpen, setIsGenerateOpen] = useState(false);
  const [previewPayslip, setPreviewPayslip] = useState(null);
  const [isSaving, setIsSaving] = useState(false);

  const currentYear = new Date().getFullYear();
  const [formData, setFormData] = useState({
    employeeId: "",
    month: String(new Date().getMonth() + 1).padStart(2, "0"),
    year: String(currentYear),
    workingDays: 26,
    presentDays: 26,
    leaveDays: 0,
    companyName: "",
    companyAddress: "",
    companyCity: "",
    companyCountry: "India",
    companyLogoUrl: "",
  });
  const [formErrors, setFormErrors] = useState({});

  // Auto-calculate attendance when employee, month, or year changes
  useEffect(() => {
    if (!isGenerateOpen || !formData.employeeId || !formData.month || !formData.year) return;

    const empLogs = attendance.filter(a => {
      if (a.employeeId !== formData.employeeId) return false;
      const [aYear, aMonth] = a.date.split("-");
      return Number(aYear) === Number(formData.year) && Number(aMonth) === Number(formData.month);
    });

    const getWorkingDaysCount = (y, m) => {
      const days = new Date(y, m, 0).getDate();
      let count = 0;
      for (let d = 1; d <= days; d++) {
        const dayOfWeek = new Date(y, m - 1, d).getDay();
        if (dayOfWeek !== 0) count++;
      }
      return count;
    };

    const calculatedWorking = getWorkingDaysCount(Number(formData.year), Number(formData.month));
    
    // 1. Absences count from attendance logs
    const absentCount = empLogs.filter(a => a.status === "absent").length;
    
    // 2. Approved leave days count from leaves collection
    let approvedLeaveCount = 0;
    const monthStart = new Date(Number(formData.year), Number(formData.month) - 1, 1);
    const monthEnd = new Date(Number(formData.year), Number(formData.month), 0);
    
    leaves.forEach(l => {
      if (l.employeeId !== formData.employeeId || l.status !== "approved") return;
      
      const start = new Date(l.fromDate);
      const end = l.toDate ? new Date(l.toDate) : start;
      
      // Find overlap
      const overlapStart = new Date(Math.max(monthStart, start));
      const overlapEnd = new Date(Math.min(monthEnd, end));
      
      if (overlapStart <= overlapEnd) {
        // Count working days (excluding Sundays) within the overlap period
        for (let d = new Date(overlapStart); d <= overlapEnd; d.setDate(d.getDate() + 1)) {
          if (d.getDay() !== 0) { // Exclude Sundays
            approvedLeaveCount++;
          }
        }
      }
    });

    const totalLeaveDays = absentCount + approvedLeaveCount;

    setFormData(prev => ({
      ...prev,
      workingDays: calculatedWorking,
      leaveDays: totalLeaveDays,
      presentDays: Math.max(0, calculatedWorking - totalLeaveDays)
    }));
  }, [isGenerateOpen, formData.employeeId, formData.month, formData.year, attendance, leaves]);

  const filtered = useMemo(() => {
    return payslips.filter((p) => {
      const q = filterEmp.toLowerCase();
      const matchEmp = !q || p.employeeName?.toLowerCase().includes(q) || p.employeeId?.toLowerCase().includes(q);
      const matchMonth = !filterMonth || p.month === filterMonth;
      const matchYear = !filterYear || p.year === filterYear;
      const matchStatus = !filterStatus || p.status === filterStatus;
      return matchEmp && matchMonth && matchYear && matchStatus;
    });
  }, [payslips, filterEmp, filterMonth, filterYear, filterStatus]);

  const stats = useMemo(() => ({
    total: payslips.length,
    generated: payslips.filter((p) => p.status === "generated").length,
    paid: payslips.filter((p) => p.status === "paid").length,
    totalNetPay: payslips.filter((p) => p.status === "paid").reduce((s, p) => s + p.netPay, 0),
  }), [payslips]);

  const yearOptions = useMemo(() => {
    const years = [];
    for (let y = currentYear - 2; y <= currentYear + 1; y++) years.push(String(y));
    return years;
  }, [currentYear]);

  const validate = (data) => {
    const errs = {};
    if (!data.employeeId) errs.employeeId = "Employee is required";
    if (!data.month) errs.month = "Month is required";
    if (!data.year) errs.year = "Year is required";
    // Check duplicate
    const dup = payslips.find(
      (p) => p.employeeId === data.employeeId && p.month === data.month && p.year === data.year
    );
    if (dup) errs.duplicate = `Payslip for this employee and month already exists (${dup.id})`;
    return errs;
  };

  const formatLogoUrl = useCallback((path) => {
    if (!path || path === "undefined" || path === "null") return "";
    if (path.startsWith("http") || path.startsWith("data:")) return path;
    let cleanPath = path.replace(/\\/g, "/");
    cleanPath = cleanPath.startsWith("/") ? cleanPath : `/${cleanPath}`;
    return `${BASE_URL}${cleanPath}`;
  }, []);

  const openGenerate = useCallback(async () => {
    // Fetch latest company settings from the API to pre-populate the form
    let companyName = "Verve Kitchens";
    let companyAddress = "";
    let companyCity = "";
    let companyCountry = "India";
    let companyLogoUrl = "";

    try {
      const res = await getCompanySettings();
      const payload = res?.data?.data || res?.data || {};
      if (payload.companyName) companyName = payload.companyName;
      if (payload.companyAddress) companyAddress = payload.companyAddress;
      if (payload.companyState) {
        companyCity = payload.companyState;
      }
      if (payload.logoExpandedPath) {
        companyLogoUrl = formatLogoUrl(payload.logoExpandedPath);
      }
    } catch (err) {
      console.warn("Failed to fetch company settings for payslip generation, using defaults", err);
    }

    setFormData({
      employeeId: "",
      month: String(new Date().getMonth() + 1).padStart(2, "0"),
      year: String(currentYear),
      workingDays: 26, presentDays: 26, leaveDays: 0,
      companyName,
      companyAddress,
      companyCity,
      companyCountry,
      companyLogoUrl,
    });
    setFormErrors({});
    setIsGenerateOpen(true);
  }, [currentYear, formatLogoUrl]);

  const closeGenerate = useCallback(() => {
    setIsGenerateOpen(false);
    setFormErrors({});
  }, []);

  const updateField = useCallback((field, value) => {
    setFormData((p) => ({ ...p, [field]: value }));
    setFormErrors((p) => ({ ...p, [field]: undefined, duplicate: undefined }));
  }, []);

  const computePayslipData = useCallback((empId, workingDays, presentDays, leaveDays) => {
    const setup = paySetups.find((s) => s.employeeId === empId);
    const emp = employees.find((e) => e.id === empId);
    if (!setup || !emp) return null;

    // Pro-rate based on attendance
    const ratio = workingDays > 0 ? presentDays / workingDays : 1;

    const components = setup.components.map((c) => {
      const baseAmount = (setup.frequency === "yearly" || setup.grossSalary > 100000) ? c.amount / 12 : c.amount;
      return {
        ...c,
        amount: c.type === "deduction" ? Math.round(baseAmount) : Math.round(baseAmount * ratio),
      };
    });

    const grossEarnings = components.filter((c) => c.type === "earning").reduce((s, c) => s + c.amount, 0);
    const totalDeductions = components.filter((c) => c.type === "deduction").reduce((s, c) => s + c.amount, 0);

    const pfRate = (setup.employerPfRate !== undefined ? setup.employerPfRate : 13) / 100;
    const esiRate = (setup.employerEsiRate !== undefined ? setup.employerEsiRate : 3.25) / 100;
    const employerPf = Math.round(grossEarnings * pfRate);
    const employerEsi = (grossEarnings > 0 && grossEarnings <= 21000) ? Math.round(grossEarnings * esiRate) : 0;

    const netPay = grossEarnings - totalDeductions;

    return { components, grossEarnings, totalDeductions, employerPf, employerEsi, netPay };
  }, [paySetups, employees]);

  const save = useCallback(async () => {
    const errs = validate(formData);
    if (Object.keys(errs).length) { setFormErrors(errs); return false; }
    setIsSaving(true);
    try {
      await new Promise((r) => setTimeout(r, 500));
      const { employeeId, month, year, workingDays, presentDays, leaveDays } = formData;
      const emp = employees.find((e) => e.id === employeeId);
      const computed = computePayslipData(employeeId, Number(workingDays), Number(presentDays), Number(leaveDays));

      if (!computed) { setFormErrors({ employeeId: "Pay setup not found for this employee. Configure Pay Setup first." }); return false; }

      const setup = paySetups.find((s) => s.employeeId === employeeId);

      generatePayslip({
        employeeId, employeeName: emp?.name || "",
        month, year,
        workingDays: Number(workingDays), presentDays: Number(presentDays), leaveDays: Number(leaveDays),
        ...computed,
        role: emp?.role || "",
        designation: emp?.role || "",
        pfNumber: setup?.pfNumber || "",
        department: emp?.department || "",
        companyDetails: {
          name: formData.companyName,
          address: formData.companyAddress,
          city: formData.companyCity,
          country: formData.companyCountry,
          logoUrl: formData.companyLogoUrl,
        }
      });
      closeGenerate();
      return true;
    } finally {
      setIsSaving(false);
    }
  }, [formData, employees, computePayslipData, generatePayslip, closeGenerate]);

  const markPaid = useCallback(async (id) => {
    await new Promise((r) => setTimeout(r, 200));
    markPayslipPaid(id);
  }, [markPayslipPaid]);

  return {
    payslips: filtered, allPayslips: payslips,
    filterEmp, setFilterEmp, filterMonth, setFilterMonth,
    filterYear, setFilterYear, filterStatus, setFilterStatus,
    stats, employees, yearOptions,
    isGenerateOpen, previewPayslip, setPreviewPayslip, isSaving,
    formData, formErrors,
    openGenerate, closeGenerate, updateField, save,
    bulkGeneratePayslips,
    markPaid, deletePayslip,
  };
}
